import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

/**
 * Exporta datos a Excel (.xlsx) con:
 * - Header rojo partido (#AF272F) con letras blancas en bold
 * - Ordenado por campos de contención: circunscripcion, entidad, distrito, ruta, seccion
 * - Números formateados como número
 * - Ancho de columnas automático
 */
export async function exportXLSX(rows, cols, filename, options = {}) {
  const { includeTotals = false } = options;
  // Las filas ya llegan filtradas y ordenadas como se muestran en la tabla.
  const sorted = [...rows];

  const wb = new ExcelJS.Workbook();
  const ws = wb.addWorksheet("Datos");

  // Header
  const headerRow = ws.addRow(cols.map(c => c.l));
  headerRow.eachCell((cell) => {
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FFAF272F" }
    };
    cell.font = { color: { argb: "FFFFFFFF" }, bold: true, size: 11 };
    cell.alignment = { horizontal: "center", vertical: "middle" };
    cell.border = {
      bottom: { style: "thin", color: { argb: "FF000000" } }
    };
  });

  // Datos
  for (const r of sorted) {
    const rowData = cols.map(c => {
      const val = r[c.k];
      if (val === null || val === undefined) return "";
      if (c.k === "ocupado") return val ? "Sí" : "No";
      if (c.k === "fecha_actualizacion") {
        const date = new Date(val);
        return Number.isNaN(date.getTime()) ? String(val) : date;
      }
      if (c.a === "right" || c.a === "center") {
        const n = Number(val);
        if (!isNaN(n) && String(val).trim() !== "") return n;
      }
      return val;
    });
    const row = ws.addRow(rowData);
    row.eachCell((cell, colNumber) => {
      const col = cols[colNumber - 1];
      if (col && ["pct_convencidos", "pct_meta_hoy"].includes(col.k) && typeof cell.value === "number") {
        cell.numFmt = '0.0"%"';
      }
      if (col?.k === "fecha_actualizacion" && cell.value instanceof Date) {
        cell.numFmt = "dd/mm/yyyy hh:mm:ss AM/PM";
      }
      if (col && (col.a === "right")) {
        cell.alignment = { horizontal: "right" };
      } else if (col && col.a === "center") {
        cell.alignment = { horizontal: "center" };
      }
    });
  }

  if (includeTotals && sorted.length > 0) {
    const sumKeys = new Set(cols.filter(c => c.a === "right").map(c => c.k));
    const totalMeta = sorted.reduce((sum, row) => sum + Number(row.meta || 0), 0);
    const totalCasa = sorted.reduce((sum, row) => sum + Number(row.convencidos_total || 0), 0);
    const totalMetaDiaria = sorted.reduce((sum, row) => sum + Number(row.meta_diaria || 0), 0);
    const totalHoy = sorted.reduce((sum, row) => sum + Number(row.conv_hoy || 0), 0);
    const totalValues = cols.map((col, index) => {
      if (index === 0) return "TOTAL";
      if (col.k === "pct_convencidos") return totalMeta > 0 ? totalCasa / totalMeta * 100 : 0;
      if (col.k === "pct_meta_hoy") return totalMetaDiaria > 0 ? totalHoy / totalMetaDiaria * 100 : 0;
      if (sumKeys.has(col.k)) return sorted.reduce((sum, row) => sum + Number(row[col.k] || 0), 0);
      return "";
    });
    const totalRow = ws.addRow(totalValues);
    totalRow.eachCell((cell, colNumber) => {
      const col = cols[colNumber - 1];
      cell.font = { bold: true };
      cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFF5F5F5" } };
      cell.border = { top: { style: "thin", color: { argb: "FFAF272F" } } };
      cell.alignment = { horizontal: col?.a === "right" ? "right" : col?.a === "center" ? "center" : "left" };
      if (col && ["pct_convencidos", "pct_meta_hoy"].includes(col.k) && typeof cell.value === "number") cell.numFmt = '0.0"%"';
    });
  }

  // Ancho de columnas automático
  ws.columns.forEach((col, i) => {
    const header = cols[i]?.l || "";
    let maxLen = header.length;
    ws.getColumn(i + 1).eachCell({ includeEmpty: false }, (cell) => {
      const len = String(cell.value || "").length;
      if (len > maxLen) maxLen = len;
    });
    col.width = Math.min(Math.max(maxLen + 3, 10), 40);
  });

  // Freeze header
  ws.views = [{ state: "frozen", ySplit: 1 }];

  // Generar y descargar
  const buffer = await wb.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
  saveAs(blob, `${filename}.xlsx`);
}
